import React from "react";
import { motion } from "framer-motion";
import { ClipboardList, Play, PauseCircle, Eye, CheckCircle2 } from "lucide-react";
import { useTaskStore } from "../../app/taskStore";

export default function TaskKpiStrip() {
  const { counts, selectedStatusFilter, setSelectedStatusFilter } = useTaskStore();

  const kpis = [
    {
      key: "TODO",
      label: "To Do",
      value: counts.TODO,
      subtext: "Tasks queued",
      icon: ClipboardList,
      bg: "bg-[#E0F2FE]", // Light blue
      iconBg: "bg-blue-200/50",
      iconColor: "text-[#0EA5E9]",
      textColor: "text-blue-900",
      subtextColor: "text-blue-600/80",
    },
    {
      key: "IN_PROGRESS",
      label: "In Progress",
      value: counts.IN_PROGRESS,
      subtext: "Currently active",
      icon: Play,
      bg: "bg-[#FEF3C7]", // Light amber
      iconBg: "bg-amber-200/50",
      iconColor: "text-[#D97706]",
      textColor: "text-amber-900",
      subtextColor: "text-amber-600/80",
    },
    {
      key: "HOLD",
      label: "Hold",
      value: counts.HOLD,
      subtext: "Paused tasks",
      icon: PauseCircle,
      bg: "bg-[#FFE4E1]", // Light red
      iconBg: "bg-red-200/50",
      iconColor: "text-[#EF4444]",
      textColor: "text-red-900",
      subtextColor: "text-red-600/80",
    },
    {
      key: "REVIEW",
      label: "Review",
      value: counts.REVIEW,
      subtext: "Awaiting review",
      icon: Eye,
      bg: "bg-[#F3E8FF]", // Light purple
      iconBg: "bg-purple-200/50",
      iconColor: "text-[#9333EA]",
      textColor: "text-purple-900",
      subtextColor: "text-purple-600/80",
    },
    {
      key: "DONE",
      label: "Done",
      value: counts.DONE,
      subtext: "Completed",
      icon: CheckCircle2,
      bg: "bg-[#E0FBEA]", // Light green
      iconBg: "bg-green-200/50",
      iconColor: "text-[#22C55E]",
      textColor: "text-green-900",
      subtextColor: "text-green-600/80",
    },
  ];

  const handleCardClick = (key) => {
    // 1. Toggle status filter
    setSelectedStatusFilter(selectedStatusFilter === key ? null : key);

    // 2. Smooth scroll if column is in DOM
    const element = document.getElementById(`task-column-${key}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      
      // Highlight effect
      element.classList.add("ring-4", "ring-indigo-500/50");
      setTimeout(() => {
        element.classList.remove("ring-4", "ring-indigo-500/50");
      }, 1500);
    } else {
      // Fallback scroll to grid for All Tasks view
      const grid = document.getElementById("all-tasks-grid");
      if (grid) {
        grid.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, idx) => {
          const isActive = selectedStatusFilter === kpi.key;
          return (
            <motion.div
              key={idx}
              onClick={() => handleCardClick(kpi.key)}
              whileHover={{
                y: -8,
                scale: 1.03,
                rotateX: 4,
                rotateY: -4,
              }}
              transition={{ type: "spring", stiffness: 180, damping: 15 }}
              className={`
                ${kpi.bg}
                rounded-2xl
                p-5
                relative
                overflow-hidden
                border-2
                ${isActive ? "border-indigo-600 shadow-[0_0_20px_rgba(99,102,241,0.5)] scale-[1.02]" : "border-white/50"}
                shadow-[0_20px_50px_rgba(45,28,106,0.3)]
                hover:shadow-[0_30px_70px_rgba(45,28,106,0.55)]
                group
                cursor-pointer
                flex flex-col justify-between
                h-36
                transition-all duration-300
              `}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* Animated Light Sweep */}
              <div className="
                absolute -inset-10
                bg-gradient-to-r
                from-transparent
                via-white/20
                to-transparent
                opacity-0
                group-hover:opacity-100
                transition
                duration-700
                rotate-12
                pointer-events-none
              "/>

              {/* Header: Icon & Active/Status indicator */}
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl ${kpi.iconBg} flex items-center justify-center shadow-sm ring-1 ring-white/50`}>
                  <kpi.icon className={kpi.iconColor} size={20} />
                </div>
                {isActive ? (
                  <span className="text-[10px] font-black uppercase text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full shadow-sm">
                    Active
                  </span>
                ) : (
                  <span className={`w-2 h-2 rounded-full ${kpi.iconColor} opacity-70 animate-pulse`} />
                )}
              </div>

              {/* Labels and values */}
              <div className="mt-3">
                <p className={`${kpi.textColor} opacity-70 text-xs font-bold uppercase tracking-wider`}>
                  {kpi.label}
                </p>
                <h3 className={`text-2xl font-black mt-0.5 tracking-tight ${kpi.textColor}`}>
                  {kpi.value}
                </h3>
                <p className={`text-[10px] mt-0.5 font-semibold ${kpi.subtextColor}`}>
                  {kpi.subtext}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
